/**
 * Contrato da compra de rotina (recorrência de lista de favoritos), E-44.
 *
 * Espelha `GET/POST/PUT/DELETE /compras-recorrentes/v1` do inversu-api
 * (API-C, task T-256). Tipagem forte aqui não é preciosismo: o projeto
 * compila com `strictTemplates: true`, então qualquer divergência entre este
 * arquivo e o backend estoura no build, não em produção — ver
 * `core/models/favorites.model.ts` para o mesmo padrão aplicado à lista de
 * favoritos.
 *
 * ⚠️ Todas as datas de **agendamento** (`data_inicio`, `proxima_execucao`,
 * `proximo_pre_aviso`, `termino.data`, `data_ocorrencia`, os itens de
 * `pular_ocorrencias`) são strings **date-only** `'YYYY-MM-DD'` — nunca
 * `Date` nem timestamp, para não sofrer deslocamento de fuso horário na
 * serialização. Os campos de auditoria (`criado_em`, `atualizado_em`,
 * `ultima_execucao_em`, `encerrada_em`) são `'YYYY-MM-DD HH:mm:ss'`.
 */

export type Periodicidade =
  | 'semanal'
  | 'quinzenal'
  | 'mensal'
  | 'bimestral'
  | 'trimestral'
  | 'semestral'
  | 'anual';

export type StatusRecorrencia = 'ativa' | 'pausada' | 'encerrada';

export type StatusOcorrenciaRecorrencia = 'criando' | 'concluida' | 'falha' | 'pulada';

export type TipoOrigemCompraRecorrente = 'bulk-list-import' | 'lista-favoritos';

export interface TerminoRecorrencia {
  tipo: 'nunca' | 'em_data' | 'apos_n';
  /** Só preenchido quando `tipo === 'em_data'`. Date-only `'YYYY-MM-DD'`. */
  data: string | null;
  /** Só preenchido quando `tipo === 'apos_n'`. */
  ocorrencias: number | null;
}

/**
 * Um item congelado da recorrência. Mesmo shape do payload de
 * `buildNegociacaoPayload` (`adiciona-negociacao-composta.component.ts`) —
 * é dali que o modo "parametrizado" (T-260) herda os campos.
 */
export interface ItemCompraRecorrente {
  ean: string;
  nome: string;
  quantidade: number;
  novo: boolean;
  usado: boolean;
  /** STRING, igual ao contrato de `POST /negociacoes/v1` — nunca `number`. */
  duracao: '1' | '3' | '5' | '7';
  valor_pretendido: number;
  encerrar_automaticamente: boolean;
}

/**
 * `itens` chega da API como **mapa** `{ "0": {...}, "1": {...} }` (update
 * por field-path no Firestore), não como array — a UI itera com
 * `itensCompraRecorrenteParaArray`.
 */
export type MapaItensCompraRecorrente = Record<string, ItemCompraRecorrente>;

export interface ItemComFalhaOcorrencia {
  index: number;
  nome: string;
  motivo?: string;
}

export interface ResumoOcorrenciaRecorrencia {
  total: number;
  sucesso: number;
  falhas: number;
  itens_com_falha?: ItemComFalhaOcorrencia[];
}

export interface OcorrenciaRecorrencia {
  /** Date-only `'YYYY-MM-DD'` — também a chave do documento em `ocorrencias/{data}`. */
  data_ocorrencia: string;
  status: StatusOcorrenciaRecorrencia;
  prompt_id: string | number | null;
  itens: MapaItensCompraRecorrente;
  resumo: ResumoOcorrenciaRecorrencia;
}

export interface OrigemCompraRecorrente {
  tipo: TipoOrigemCompraRecorrente;
  /** Só quando `tipo === 'bulk-list-import'`. */
  prompt_id?: string | number | null;
  /** Só quando `tipo === 'lista-favoritos'` — nome da lista, não um id: o
   * vínculo é só informativo (ver T-261/T-262, renomear a lista quebra o selo). */
  lista_favoritos?: string | null;
}

/**
 * Item ainda não parametrizado — o que a lista de favoritos guarda (só
 * EAN+nome+imagem). No modo "cru" o formulário expõe
 * quantidade/novo-usado/duração/valor pretendido/encerrar automaticamente
 * por item, porque nada disso existe na origem.
 *
 * Movido de `tornar-recorrente-modal.component.ts` para cá em T-271 (junto
 * com `ModoTornarRecorrenteModal`/`TornarRecorrenteModalData`), quando o
 * formulário foi extraído para `TornarRecorrenteFormComponent` — o shell
 * (`TornarRecorrenteModalComponent`) reexporta os três para não quebrar
 * `favorites.component.ts`/`listas-favoritos.component.ts`, que importam
 * `ItemTornarRecorrenteCru` de lá.
 */
export interface ItemTornarRecorrenteCru {
  ean: string;
  nome: string;
  imagemUrl?: string | null;
}

export type ModoTornarRecorrenteModal = 'parametrizado' | 'cru';

/**
 * Dados de abertura do modal `TornarRecorrenteModalComponent` — `{ itens,
 * origem, nomeSugerido }` via `MAT_DIALOG_DATA`, conforme T-257/T-260/T-261.
 *
 * - `origem.tipo === 'bulk-list-import'` (vem do stepper,
 *   `adiciona-negociacao-composta`) ⇒ modo **parametrizado**: `itens` já
 *   são `ItemCompraRecorrente[]` congelados (mesmo shape de
 *   `buildNegociacaoPayload`), o modal só revisa.
 * - `origem.tipo === 'lista-favoritos'` (vem das telas de favoritos) ⇒ modo
 *   **cru**: `itens` são `ItemTornarRecorrenteCru[]`, e o formulário
 *   coleta os campos que faltam por item.
 */
export interface TornarRecorrenteModalData {
  itens: ItemCompraRecorrente[] | ItemTornarRecorrenteCru[];
  origem: OrigemCompraRecorrente;
  /** Nome sugerido para a rotina — ex.: o nome da lista de favoritos ou um
   * default calculado pelo caller. Editável no formulário. */
  nomeSugerido?: string;
  /**
   * Presente só ao reabrir o modal para **editar** uma compra de rotina já
   * existente (T-262, ação "Editar" da tela de gestão) — o caller busca o
   * detalhe (`detalheCompraRecorrente`) antes de abrir, porque a listagem só
   * tem o resumo. Quando preenchido: `nome`/`periodicidade`/`data_inicio`/
   * `termino`/`pre_aviso_dias`/`itens` pré-preenchem o formulário (`itens`
   * e `origem` do resto de `data` são ignorados), o modo vira sempre "cru"
   * (todo item editável — a compra já existente tem os campos completos
   * independente da origem original) e `salvar()` chama
   * `atualizarCompraRecorrente` em vez de `criarCompraRecorrente`.
   */
  compraExistente?: CompraRecorrente;
}

export interface CompraRecorrente {
  id: string;
  uid: string;
  nome: string;
  status: StatusRecorrencia;
  periodicidade: Periodicidade;
  /** Dia do mês (mensal+) ou dia da semana (semanal/quinzenal) derivado de `data_inicio`. */
  dia_referencia: number;
  data_inicio: string;
  termino: TerminoRecorrencia;
  ocorrencias_realizadas: number;
  proxima_execucao: string;
  proximo_pre_aviso: string;
  pre_aviso_dias: number;
  /** Datas date-only puladas via `pular-proxima`. */
  pular_ocorrencias: string[];
  /** Contador de execuções seguidas sem sucesso (T-259). `>= 3` é o sinal
   * client-side de "pausada automaticamente" — não existe um status/booleano
   * dedicado (decisão da T-256, ver `docs/design/compra-de-rotina-ux-spec.md` §5/§9).
   * Só existe no detalhe (`GET /:id`) — o resumo da listagem NÃO devolve
   * este campo, ver `CompraRecorrenteResumo`. */
  falhas_consecutivas: number;
  itens: MapaItensCompraRecorrente;
  total_itens: number;
  origem: OrigemCompraRecorrente;
  criado_em: string;
  atualizado_em: string;
  ultima_execucao_em: string | null;
  encerrada_em: string | null;
}

/** `GET /compras-recorrentes/v1/:id` acrescenta as últimas ocorrências ao detalhe. */
export interface DetalheCompraRecorrente extends CompraRecorrente {
  ultimas_ocorrencias?: OcorrenciaRecorrencia[];
}

/** Query string aceita por `GET /compras-recorrentes/v1`. */
export interface FiltroComprasRecorrentes {
  skip: number;
  top: number;
  status?: StatusRecorrencia | '';
}

/** Um item do resumo de 3 primeiros (`_itensResumo` no backend) — só o
 * suficiente pro "e mais N" do card da listagem, sem imagem (a API não
 * devolve `imagemUrl` aqui). */
export interface ItemResumoCompraRecorrente {
  nome: string | null;
  quantidade: number | null;
}

/**
 * Shape real de cada item de `GET /compras-recorrentes/v1` (listagem) —
 * `ComprasRecorrentesModel._resumo()` no `inversu-api` (T-256). **Não** é
 * `CompraRecorrente` — o endpoint de listagem é uma projeção leve — só os 3
 * primeiros itens resumidos (`itens_resumo`, nunca o mapa `itens` completo)
 * e sem `dia_referencia`/`termino`/`pre_aviso_dias`/`pular_ocorrencias`/
 * `encerrada_em`/`falhas_consecutivas`. Só o `GET /:id`
 * (`DetalheCompraRecorrente`) devolve o documento inteiro — o drill-in
 * busca o detalhe à parte antes de exibir itens completos, editar ou
 * distinguir pausa manual de auto-pausa.
 *
 * ⚠️ `origem` está declarado aqui **opcional** por causa do consumidor de
 * T-261 (`favorites.component.ts`/`listas-favoritos.component.ts`, selo
 * "Recorrente"), mas **conferido contra o código real do backend
 * (`compras-recorrentes-model.js:_resumo`, T-256) o campo NÃO é devolvido
 * pela listagem hoje** — só o `GET /:id` tem `origem`. Isso quebra em
 * runtime (não em build) o cruzamento `origem.lista_favoritos` que o selo
 * depende — reportado como gap na Conclusão da T-262 para `naruto-san`
 * incluir `origem` (ou só `origem.lista_favoritos`) na projeção da
 * listagem. Deixei opcional (em vez de remover) só para o build não quebrar
 * nos dois consumidores paralelos; não invente um valor aqui.
 */
export interface CompraRecorrenteResumo {
  id: string;
  nome: string;
  status: StatusRecorrencia;
  periodicidade: Periodicidade;
  data_inicio: string;
  proxima_execucao: string;
  proximo_pre_aviso: string;
  itens_resumo: ItemResumoCompraRecorrente[];
  total_itens: number;
  ocorrencias_realizadas: number;
  /** Ver o aviso acima — hoje sempre `undefined` na prática (gap de backend). */
  origem?: OrigemCompraRecorrente;
  criado_em: string;
  atualizado_em: string;
  ultima_execucao_em: string | null;
}

export interface PaginaComprasRecorrentes {
  total: number;
  skip: number;
  top: number;
  itens: CompraRecorrenteResumo[];
}

/**
 * Body de `POST /compras-recorrentes/v1`. Ao contrário do que a API devolve
 * na leitura, `itens` entra como **array** aqui (é o que o formulário/stepper
 * produz) — o backend converte para o mapa `"0".."n-1"` na escrita.
 */
export interface CriacaoCompraRecorrente {
  nome: string;
  periodicidade: Periodicidade;
  data_inicio: string;
  termino: TerminoRecorrencia;
  pre_aviso_dias: number;
  itens: ItemCompraRecorrente[];
  origem: OrigemCompraRecorrente;
}

/** Body de `PUT /compras-recorrentes/v1/:id` — mesmos campos, todos opcionais. */
export type AtualizacaoCompraRecorrente = Partial<CriacaoCompraRecorrente>;

/** Converte o mapa `itens` (contrato de leitura da API) em array ordenado
 * pelo índice — o shape que a UI de fato itera. */
export function itensCompraRecorrenteParaArray(
  itens: MapaItensCompraRecorrente | null | undefined
): ItemCompraRecorrente[] {
  if (!itens) return [];
  return Object.keys(itens)
    .sort((a, b) => Number(a) - Number(b))
    .map(chave => itens[chave]);
}
